import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { trackAddToCart } from "~/lib/facebook-pixel";
import {
  mapShopCartItemToGA4Item,
  trackGA4AddToCart,
  trackGA4RemoveFromCart,
} from "~/lib/gtm-datalayer";
import { ShopCart, ShopCartItem } from "~/types/shop";

interface GiftData {
  isGift: boolean;
  recipientName: string;
  recipientEmail: string;
  message: string;
  giftCardTemplateId: number | null;
  giftCardBackgroundUrl: string;
  giftCardContentBackgroundUrl: string;
  giftCardMessage: string;
  giftCardGreeting: string;
  giftCardFontFamily: string;
}

interface CouponData {
  code: string | null;
  discount: number;
}

interface ShopCartState {
  shopCart: ShopCart;
  pendingOrderId: number | null;
  giftData: GiftData;
  couponData: CouponData;
  addToCart: (item: Omit<ShopCartItem, "quantity">) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
  setPendingOrderId: (orderId: number | null) => void;
  setGiftData: (giftData: Partial<GiftData>) => void;
  setCouponData: (couponData: Partial<CouponData>) => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

const initialCart: ShopCart = {
  items: [],
  total: 0,
  currency: "EUR",
  item_count: 0,
};

export const useShopCartStore = create<ShopCartState>()(
  devtools(
    persist(
      (set, get) => ({
        shopCart: initialCart,
        pendingOrderId: null,
        giftData: {
          isGift: false,
          recipientName: "",
          recipientEmail: "",
          message: "",
          giftCardTemplateId: null,
          giftCardBackgroundUrl: "",
          giftCardContentBackgroundUrl: "",
          giftCardMessage: "",
          giftCardGreeting: "",
          giftCardFontFamily: "",
        },
        couponData: {
          code: null,
          discount: 0,
        },

        addToCart: (item) => {
          const state = get();
          const existingItem = state.shopCart.items.find(
            (cartItem) => cartItem.id === item.id
          );

          let newItems: ShopCartItem[];

          if (existingItem) {
            // Aggiorna la quantità se l'item esiste già
            newItems = state.shopCart.items.map((cartItem) =>
              cartItem.id === item.id
                ? { ...cartItem, quantity: cartItem.quantity + 1 }
                : cartItem
            );
          } else {
            // Aggiungi nuovo item
            newItems = [...state.shopCart.items, { ...item, quantity: 1 }];
          }

          const total = newItems.reduce(
            (sum, cartItem) => sum + cartItem.price * cartItem.quantity,
            0
          );
          const item_count = newItems.reduce(
            (sum, cartItem) => sum + cartItem.quantity,
            0
          );

          set({
            shopCart: {
              items: newItems,
              total,
              currency: "EUR",
              item_count,
            },
          });

          try {
            trackAddToCart({
              content_name: item.name,
              content_ids: [item.id.toString()],
              currency: "EUR",
              value: item.price,
              num_items: 1,
            });

            trackGA4AddToCart(
              [mapShopCartItemToGA4Item({ ...item, quantity: 1 })],
              item.price,
            );
          } catch (error) {
            console.error("Error tracking AddToCart:", error);
          }
        },

        removeFromCart: (id) => {
          const state = get();
          const removedItem = state.shopCart.items.find(
            (item) => item.id === id
          );
          const newItems = state.shopCart.items.filter(
            (item) => item.id !== id
          );
          const total = newItems.reduce(
            (sum, cartItem) => sum + cartItem.price * cartItem.quantity,
            0
          );
          const item_count = newItems.reduce(
            (sum, cartItem) => sum + cartItem.quantity,
            0
          );

          if (removedItem) {
            try {
              trackGA4RemoveFromCart(
                [mapShopCartItemToGA4Item(removedItem)],
                removedItem.price * removedItem.quantity,
              );
            } catch (error) {
              console.error("Error tracking GA4 remove_from_cart:", error);
            }
          }

          set({
            shopCart: {
              items: newItems,
              total,
              currency: "EUR",
              item_count,
            },
          });
        },

        updateQuantity: (id, quantity) => {
          if (quantity <= 0) {
            get().removeFromCart(id);
            return;
          }

          const state = get();
          const newItems = state.shopCart.items.map((item) =>
            item.id === id ? { ...item, quantity } : item
          );
          const total = newItems.reduce(
            (sum, cartItem) => sum + cartItem.price * cartItem.quantity,
            0
          );
          const item_count = newItems.reduce(
            (sum, cartItem) => sum + cartItem.quantity,
            0
          );

          set({
            shopCart: {
              items: newItems,
              total,
              currency: "EUR",
              item_count,
            },
          });
        },

        clearCart: () => {
          set({
            shopCart: initialCart,
            pendingOrderId: null,
            giftData: {
              isGift: false,
              recipientName: "",
              recipientEmail: "",
              message: "",
              giftCardTemplateId: null,
              giftCardBackgroundUrl: "",
              giftCardContentBackgroundUrl: "",
              giftCardMessage: "",
              giftCardGreeting: "",
              giftCardFontFamily: "",
            },
            couponData: {
              code: null,
              discount: 0,
            },
          });
        },

        setGiftData: (giftData) => {
          set((state) => ({
            giftData: { ...state.giftData, ...giftData },
          }));
        },

        setCouponData: (couponData) => {
          set((state) => ({
            couponData: { ...state.couponData, ...couponData },
          }));
        },

        setPendingOrderId: (orderId) => {
          set({ pendingOrderId: orderId });
        },

        getTotalItems: () => {
          return get().shopCart.item_count;
        },

        getTotalPrice: () => {
          return get().shopCart.total;
        },
      }),
      {
        name: "shop-cart-storage",
        storage: createJSONStorage(() => localStorage),
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<ShopCartState>;
          return {
            ...currentState,
            ...persisted,
            giftData: {
              ...currentState.giftData,
              ...persisted.giftData,
            },
            couponData: {
              ...currentState.couponData,
              ...persisted.couponData,
            },
          };
        },
      }
    )
  )
);
